import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState } from 'react';
import { toast } from 'sonner';
import * as QRCode from 'qrcode';
import {
  Archive,
  ArchiveRestore,
  Loader2,
  Pencil,
  Plus,
  QrCode as QrIcon,
  Share2,
  StickyNote,
  Trash2,
} from 'lucide-react';
import { api } from '@/lib/api';
import type { CampaignStatsResponse } from '@/lib/api';
import { BottomSheet } from '@/components/ui/dialog';
import {
  Skeleton,
  SkeletonCard,
  SkeletonLine,
  SkeletonPage,
  SkeletonStats,
} from '@/components/ui/skeleton';
import { getWebApp, haptic } from '@/lib/telegram';
import { CopyLinkRow, RoiPill, fmtRub, fmtSpend, usdMinorToRub } from './campaigns';

const CampaignChart = lazy(() =>
  import('@/components/DashboardCharts').then((m) => ({ default: m.CampaignChart })),
);
const TopPlansPie = lazy(() =>
  import('@/components/DashboardCharts').then((m) => ({ default: m.TopPlansPie })),
);

export const Route = createFileRoute('/campaigns/$campaignId')({ component: CampaignDetail });

type Period = 'week' | 'month' | '2m' | 'all';
const PERIOD_DAYS: Record<Period, number> = { week: 7, month: 30, '2m': 60, all: 0 };
const PERIOD_LABELS: Record<Period, string> = {
  week: 'Неделя',
  month: 'Месяц',
  '2m': '2 месяца',
  all: 'Всё время',
};

function CampaignDetail() {
  const { campaignId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [period, setPeriod] = useState<Period>('month');
  const [spendOpen, setSpendOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['campaign-stats', campaignId, period],
    queryFn: () => api.campaignStats(campaignId, PERIOD_DAYS[period]),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/campaigns' });
    tg.BackButton.onClick(onBack);
    tg.MainButton?.hide();
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const archiveMutation = useMutation({
    mutationFn: (status: 'active' | 'archived') => api.updateCampaign(campaignId, { status }),
    onSuccess: () => {
      haptic('success');
      void qc.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Не получилось'),
  });

  const deleteSpendMutation = useMutation({
    mutationFn: (entryId: string) => api.deleteCampaignSpend(campaignId, entryId),
    onSuccess: () => {
      haptic('success');
      toast.success('Расход удалён');
      void qc.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Не получилось'),
  });

  if (!data || !data.campaign.id) return <DetailSkeleton />;

  const c = data.campaign;
  const fx = data.fx_rate_to_display > 0 ? data.fx_rate_to_display : 100;
  const archived = c.status === 'archived';

  const chartData = data.daily.map((d) => ({
    day: d.day,
    starts: d.starts,
    revenue: usdMinorToRub(d.revenue_minor, fx),
    margin: usdMinorToRub(d.margin_minor, fx),
  }));
  const hasChartData = data.daily.some((d) => d.starts > 0 || d.purchases > 0);
  const pieData = data.plans.map((p) => ({ name: p.plan_name, value: p.sales_count }));

  const share = () => {
    if (!c.link) return;
    getWebApp()?.openTelegramLink(
      `https://t.me/share/url?url=${encodeURIComponent(c.link)}&text=${encodeURIComponent('Надёжный VPN — подключение за минуту')}`,
    );
  };

  return (
    <div className="p-4 space-y-3 pb-28">
      <div className="flex items-start justify-between gap-2 px-1 pt-2">
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          aria-label="Переименовать кампанию"
          className="min-w-0 flex-1 text-left active:opacity-70 transition-opacity"
        >
          <h2 className="font-display text-xl font-bold tracking-tight flex items-start gap-1.5 min-w-0">
            <span className="line-clamp-2 [overflow-wrap:anywhere]">{c.name}</span>
            <Pencil className="w-3.5 h-3.5 shrink-0 text-dim mt-1.5" aria-hidden />
            {archived && <span className="text-dim font-normal text-sm shrink-0">архив</span>}
          </h2>
          <div className="text-[11px] text-dim truncate mt-0.5">
            {c.bot_username ? `@${c.bot_username}` : 'бот удалён'}
          </div>
        </button>
        <RoiPill roi={c.roi_percent} />
      </div>

      {/* Ссылка */}
      <div className="surface-card rounded-[22px] p-[18px] space-y-3">
        {c.link ? (
          <>
            <CopyLinkRow link={c.link} />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={share}
                className="flex-1 bg-primary text-primary-foreground py-3 rounded-[14px] text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
              >
                <Share2 className="w-4 h-4" />
                Поделиться
              </button>
              <QrBlockButton link={c.link} />
            </div>
          </>
        ) : (
          <div className="text-xs text-amber">
            Бот кампании удалён — ссылка недоступна, статистика сохранена.
          </div>
        )}
        {c.site_link ? (
          <div className="space-y-1.5">
            <div className="text-[11px] text-dim">Ссылка на сайт</div>
            <CopyLinkRow link={c.site_link} />
          </div>
        ) : null}
        <p className="text-[11px] text-dim">
          Клиент попадает в кампанию при первом заходе в этого бота
          {c.site_link ? ' или при регистрации на сайте по ссылке' : ''}. Кто уже открывал бот раньше — не засчитается.
        </p>
      </div>

      {/* Заметка (#228): где куплена реклама, креатив, договорённости */}
      <button
        type="button"
        onClick={() => setEditOpen(true)}
        className="w-full surface-card rounded-[22px] p-[18px] text-left active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <StickyNote className="w-3.5 h-3.5" aria-hidden />
          Заметка
        </div>
        {c.note ? (
          <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap break-words line-clamp-6">
            {c.note}
          </p>
        ) : (
          <p className="mt-2 text-sm text-dim">
            Где куплена реклама, сколько стоила, какой креатив. Видите только вы.
          </p>
        )}
      </button>

      {/* Ключевые цифры (за всё время) */}
      <div className="grid grid-cols-2 gap-2.5">
        <StatBox
          label="Пришло"
          value={String(c.starts)}
          sub={c.site_starts ? `с сайта ${c.site_starts}` : undefined}
        />
        {c.site_link || c.site_visits ? (
          <StatBox label="Заходы на сайт" value={String(c.site_visits ?? 0)} sub="уникальные за сутки" />
        ) : null}
        <StatBox
          label="Купили"
          value={`${c.buyers}`}
          sub={c.starts > 0 ? `конверсия ${Math.round(c.conversion_percent)}%` : undefined}
        />
        <StatBox
          label="Покупок"
          value={String(c.purchases)}
          sub={`платных ${c.paid_purchases} · триалов ${c.trials}`}
        />
        <StatBox label="Оборот" value={fmtRub(usdMinorToRub(c.revenue_minor, fx))} tone="primary" />
        <StatBox label="Маржа" value={fmtRub(usdMinorToRub(c.margin_minor, fx))} tone="money" />
        <StatBox
          label="Потрачено"
          value={fmtSpend(c.spend_by_currency)}
          sub={c.spend_usd_minor > 0 ? `≈ ${fmtRub(usdMinorToRub(c.spend_usd_minor, fx))}` : 'добавьте расходы для ROI'}
          tone="amber"
        />
      </div>

      {/* Период для графика/планов */}
      <div className="flex gap-1.5">
        {(Object.keys(PERIOD_DAYS) as Period[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            className={`flex-1 py-2 rounded-[11px] text-[11.5px] font-bold transition-colors ${
              period === p ? 'bg-white/[0.12] text-foreground' : 'surface-inset text-muted-foreground'
            }`}
          >
            {PERIOD_LABELS[p]}
          </button>
        ))}
      </div>

      {/* График */}
      <div className="surface-card rounded-[22px] p-[18px] space-y-2">
        <h3 className="font-display font-semibold text-sm">Динамика</h3>
        {hasChartData ? (
          <div className="h-44 -mx-2">
            <Suspense fallback={<Skeleton className="h-full w-full" />}>
              <CampaignChart data={chartData} />
            </Suspense>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground py-4 text-center">
            Пока нет данных за период. Поделитесь ссылкой, и они появятся.
          </p>
        )}
        <div className="flex items-center gap-4 text-[10.5px] text-dim">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-[3px] rounded bg-white/80 inline-block" /> Оборот
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-[3px] rounded bg-money-light inline-block" /> Переходы
          </span>
        </div>
      </div>

      {/* Планы */}
      {pieData.length > 0 && (
        <div className="surface-card rounded-[22px] p-[18px] space-y-2">
          <h3 className="font-display font-semibold text-sm">Что покупают</h3>
          <div className="h-44">
            <Suspense fallback={<Skeleton className="h-full w-full" />}>
              <TopPlansPie data={pieData} />
            </Suspense>
          </div>
        </div>
      )}

      {/* Журнал расходов */}
      <div className="surface-card rounded-[22px] p-[18px] space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-sm">Расходы на закуп</h3>
          <button
            type="button"
            onClick={() => setSpendOpen(true)}
            className="text-primary-light text-xs font-bold flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Добавить
          </button>
        </div>
        {data.spend_entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Добавьте траты на рекламу, и ROI посчитается сам.
          </p>
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {data.spend_entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 py-2.5">
                <div>
                  <div className="text-sm font-bold nums">
                    {e.currency === 'RUB'
                      ? `${Math.round(e.amount_minor / 100).toLocaleString('ru')} ₽`
                      : `$${(e.amount_minor / 100).toLocaleString('ru')}`}
                  </div>
                  <div className="text-[10.5px] text-dim mt-0.5">
                    {formatDate(e.spent_on)}
                    {e.comment ? ` · ${e.comment}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => deleteSpendMutation.mutate(e.id)}
                  disabled={deleteSpendMutation.isPending}
                  aria-label="Удалить расход"
                  className="relative p-2 text-dim hover:text-danger transition-colors after:absolute after:-inset-1.5 after:content-['']"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Архив */}
      <button
        type="button"
        onClick={() => archiveMutation.mutate(archived ? 'active' : 'archived')}
        disabled={archiveMutation.isPending}
        className="w-full surface-card rounded-[16px] py-3 text-sm font-bold text-muted-foreground flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      >
        {archiveMutation.isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : archived ? (
          <ArchiveRestore className="w-4 h-4" />
        ) : (
          <Archive className="w-4 h-4" />
        )}
        {archived ? 'Вернуть из архива' : 'В архив'}
      </button>

      <EditCampaignSheet
        open={editOpen}
        onClose={() => setEditOpen(false)}
        campaignId={c.id}
        name={c.name}
        note={c.note ?? ''}
      />
      <AddSpendSheet
        open={spendOpen}
        onClose={() => setSpendOpen(false)}
        campaignId={campaignId}
        onAdded={() => {
          setSpendOpen(false);
          void qc.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
          void qc.invalidateQueries({ queryKey: ['campaigns'] });
        }}
      />
    </div>
  );
}

function StatBox({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'primary' | 'money' | 'amber';
}) {
  const toneCls =
    tone === 'money'
      ? 'text-money-light'
      : tone === 'amber'
        ? 'text-amber'
        : tone === 'primary'
          ? 'text-primary-light'
          : 'text-foreground';
  return (
    <div className="surface-card rounded-[18px] p-3.5">
      <div className="text-[10.5px] text-dim">{label}</div>
      <div className={`font-display text-[19px] font-bold nums mt-1 ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-dim mt-0.5">{sub}</div>}
    </div>
  );
}

function QrBlockButton({ link }: { link: string }) {
  const [open, setOpen] = useState(false);
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    if (!open || svg) return;
    void QRCode.toString(link, { type: 'svg', errorCorrectionLevel: 'M', margin: 1 }).then(setSvg);
  }, [open, svg, link]);

  const download = async () => {
    haptic('selection');
    const dataUrl = await QRCode.toDataURL(link, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 1024,
    });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'campaign-qr.png';
    a.click();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="QR-код ссылки"
        className="bg-white/5 px-4 rounded-[14px] text-sm font-bold flex items-center justify-center hover:bg-white/[0.08] transition-colors"
      >
        <QrIcon className="w-4 h-4" />
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} title="QR-код кампании">
        <div className="bg-white p-4 rounded-xl flex flex-col items-center gap-3">
          {svg ? (
            <div className="w-56 h-56" dangerouslySetInnerHTML={{ __html: svg }} />
          ) : (
            <Skeleton tone="light" className="w-56 h-56 rounded" />
          )}
          <button type="button" onClick={download} className="text-xs font-bold text-foreground">
            Скачать PNG ↓
          </button>
        </div>
      </BottomSheet>
    </>
  );
}

function AddSpendSheet({
  open,
  onClose,
  campaignId,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  onAdded: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<'RUB' | 'USD'>('RUB');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [comment, setComment] = useState('');

  const parsed = Number(amount.replace(',', '.').replace(/\s/g, ''));
  const valid = Number.isFinite(parsed) && parsed > 0;

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.addCampaignSpend(campaignId, {
        spent_on: date,
        amount_minor: Math.round(parsed * 100),
        currency,
        comment: comment.trim() || undefined,
      }),
    onSuccess: () => {
      haptic('success');
      toast.success('Расход добавлен');
      setAmount('');
      setComment('');
      onAdded();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось добавить расход');
    },
  });

  return (
    <BottomSheet open={open} onClose={onClose} title="Расход на рекламу">
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Сумма
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="1500"
            className="flex-1 surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none focus:border-white/30"
          />
          <div className="flex rounded-[13px] overflow-hidden surface-inset">
            {(['RUB', 'USD'] as const).map((cur) => (
              <button
                key={cur}
                type="button"
                onClick={() => setCurrency(cur)}
                className={`px-4 text-sm font-bold transition-colors ${
                  currency === cur ? 'bg-white/[0.12] text-foreground' : 'text-muted-foreground'
                }`}
              >
                {cur === 'RUB' ? '₽' : '$'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Дата
        </label>
        <input
          type="date"
          value={date}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setDate(e.target.value)}
          className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none focus:border-white/30"
        />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Комментарий <span className="text-dim normal-case font-normal">— необязательно</span>
        </label>
        <input
          type="text"
          maxLength={256}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="закуп в канале Х"
          className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none focus:border-white/30"
        />
      </div>

      <button
        type="button"
        disabled={!valid || isPending}
        onClick={() => mutate()}
        className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        Добавить расход
      </button>
    </BottomSheet>
  );
}

const NAME_MAX = 128;
const NOTE_MAX = 1000;

/** Правка названия и заметки. Код в ссылке не меняется: на него ведут опубликованные посты. */
function EditCampaignSheet({
  open,
  onClose,
  campaignId,
  name,
  note,
}: {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  name: string;
  note: string;
}) {
  const qc = useQueryClient();
  const [draftName, setDraftName] = useState(name);
  const [draftNote, setDraftNote] = useState(note);

  useEffect(() => {
    if (!open) return;
    setDraftName(name);
    setDraftNote(note);
  }, [open, name, note]);

  const trimmedName = draftName.trim();
  const changed = trimmedName !== name || draftNote.trim() !== note.trim();
  const valid = trimmedName.length > 0;
  const left = NOTE_MAX - draftNote.length;

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.updateCampaign(campaignId, { name: trimmedName, note: draftNote.trim() || null }),
    onSuccess: (saved) => {
      haptic('success');
      qc.setQueriesData<CampaignStatsResponse>(
        { queryKey: ['campaign-stats', campaignId] },
        (old) =>
          old ? { ...old, campaign: { ...old.campaign, name: saved.name, note: saved.note } } : old,
      );
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Сохранено');
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не получилось сохранить');
    },
  });

  return (
    <BottomSheet open={open} onClose={onClose} title="Кампания">
      <div className="space-y-2">
        <label
          htmlFor="campaign-name"
          className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          Название
        </label>
        <input
          id="campaign-name"
          type="text"
          value={draftName}
          maxLength={NAME_MAX}
          onChange={(e) => setDraftName(e.target.value)}
          className="w-full surface-inset rounded-[13px] px-4 py-3 text-base focus:outline-none focus:border-white/30"
        />
      </div>

      <div className="space-y-2">
        <label
          htmlFor="campaign-note"
          className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          Заметка <span className="text-dim normal-case font-normal">— видите только вы</span>
        </label>
        <textarea
          id="campaign-note"
          value={draftNote}
          maxLength={NOTE_MAX}
          rows={5}
          onChange={(e) => setDraftNote(e.target.value)}
          placeholder="Где куплена реклама, сколько стоила, какой креатив, о чём договорились"
          className="w-full surface-inset rounded-[13px] px-4 py-3 text-base leading-relaxed resize-none focus:outline-none focus:border-white/30"
        />
        {left < 200 ? (
          <div className={`text-[11px] tabular-nums ${left < 100 ? 'text-amber' : 'text-dim'}`}>
            Осталось знаков: {left}
          </div>
        ) : null}
      </div>

      <p className="text-[11px] text-dim">
        Ссылка кампании не меняется: старые посты продолжают считаться.
      </p>

      <button
        type="button"
        disabled={!valid || !changed || isPending}
        onClick={() => mutate()}
        className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        Сохранить
      </button>
    </BottomSheet>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

/** Название кампании, карточка ссылки, четыре плитки цифр, график. */
function DetailSkeleton() {
  return (
    <SkeletonPage className="space-y-3">
      <div className="px-1 pt-2 space-y-2">
        <SkeletonLine size="title" className="w-48" />
        <SkeletonLine size="xs" className="w-28" />
      </div>
      <SkeletonCard className="rounded-[22px] min-h-0 p-4 space-y-3">
        <SkeletonLine size="xs" className="w-24" />
        <Skeleton className="h-11 w-full rounded-[14px]" />
      </SkeletonCard>
      <SkeletonStats count={4} cols={2} className="gap-2.5" />
      <SkeletonCard className="h-52 rounded-[22px]" />
    </SkeletonPage>
  );
}
